import React, { useEffect } from "react";
import { useDispatch } from "react-redux";

import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { ContainerContent } from "@/components/Common/Dashboard/ContainerContent";
import { ContainerTitle } from "@/components/Common/Dashboard/ContainerTitle";
import { ProfileSkeleton } from "@/portals/(shared)/(profile)/components/ProfileSkeleton";
import { setDynamicRouteConfiguration } from "@/redux/actions/navbar";
import { showToast } from "@/providers/ToastProvider";

import { BasicInfoForm } from "./components/BasicInfoForm";
import { EmergencyContactSection } from "./components/EmergencyContactSection";
import type { UpdatePatientProfileVariables } from "./api/queryFunctions/updatePatientProfile";
import { usePatientProfile } from "./api/queries/usePatientProfile";
import { useUpdatePatientProfile } from "./api/mutations/useUpdatePatientProfile";

export default function PatientProfile() {
  const dispatch = useDispatch();
  const { data: profileData, isLoading } = usePatientProfile();
  const { mutate: updateProfile } = useUpdatePatientProfile();

  useEffect(() => {
    dispatch(setDynamicRouteConfiguration({ logoSection: { title: "Profile" } }));

    return () => {
      dispatch(setDynamicRouteConfiguration({}));
    };
  }, [dispatch]);

  const submitHandler = (
    formData: UpdatePatientProfileVariables,
    actions: { setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    updateProfile(formData, {
      onError: (error) => {
        actions.setSubmitting(false);
        captureErrorMessage(parseError({ error, fallback: "Failed to update patient profile" }), {
          tags: { component: "PatientProfile", errorType: "submit_profile" },
        });
        showToast([{ heading: "Failed to update profile.", variant: "error" }]);
      },
      onSuccess: () => {
        actions.setSubmitting(false);
      },
    });
  };

  if (isLoading || !profileData) return <ProfileSkeleton segments={["content"]} />;

  return (
    <ContainerContent>
      <ContainerTitle title="Profile" />

      <BasicInfoForm />
      <EmergencyContactSection data={profileData} submit={submitHandler} />
    </ContainerContent>
  );
}
